import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex column no-wrap full-width full-height"
}
const _hoisted_2 = {
  key: 1,
  class: "flex column no-wrap full-width full-height items-center justify-center"
}
const _hoisted_3 = { class: "column absolute-full flex-center transparent" }
const _hoisted_4 = { class: "text-error" }
const _hoisted_5 = { class: "text-caption text-navy-base q-mb-lg" }
const _hoisted_6 = { class: "absolute-full column flex-center transparent image-gallery-item--error" }
const _hoisted_7 = { class: "text-error" }

import { App } from "@src-capacitor/@capacitor/app"
import { PluginListenerHandle } from "@src-capacitor/@capacitor/core"
import PinchZoom from "pinch-zoom-js"
import { date, QImg, useDialogPluginComponent } from "quasar"
import SfxTopNavigation from "src/components/navigation/SfxTopNavigation.vue"
import { DATE_TIME_FULL_MASK } from "src/util/constants/configurations"
import { ContextAction } from "src/util/enums/entityType"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { NavigationAction } from "src/util/enums/topNavigation"
import { ImageViewerItem } from "src/util/types/components/sfxImageGallery"
import { computed, onMounted, onUnmounted, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import SfxBtnOutline from "../button/SfxBtnOutline.vue"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxImageViewOverlay',
  props: {
  /**
   * List of images that should be shown in the gallery.
   */
  images: { type: Array as PropType<Array<ImageViewerItem>>, required: true },

  /**
   * The index of the images within `images` to be selected initially.
   */
  selectedIdx: { type: Number, required: true },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const { dialogRef, onDialogCancel } = useDialogPluginComponent()
const { t } = useI18n()

const selectedIdx = ref(clampImageIdx(props.selectedIdx))
const selectedImage = computed(() => props.images[selectedIdx.value])

// Indicates if the user has zoomed in on the selected image.
// If there is only a single image in the gallery we are always zoomed.
const isZoomedIn = ref(props.images.length === 1)

let androidBackButtonListener: PluginListenerHandle | undefined
const pinchZoomTarget = ref<QImg | null>(null)
let pinchZoom: PinchZoom | undefined

//endregion

//region Lifecycle

onMounted(async () => {
  // Add android back button listener
  if (process.env.MODE === "capacitor") {
    androidBackButtonListener = await App.addListener("backButton", onAndroidBack)
  }
})

onUnmounted(async () => {
  // Remove listener on android back button.
  if (process.env.MODE === "capacitor") {
    await androidBackButtonListener?.remove()
  }
  // Destroy pinch zoom instance.
  pinchZoom?.destroy()
})

// endregion

//region Functions

/**
 * Helper for clamping an index to the range of props.images.
 * Used to guarantee that props.selectedIdx always points to and index in props.images.
 */
function clampImageIdx(idx: number) {
  return Math.max(Math.min(idx, props.images.length), 0)
}

/**
 * Go to an image in the carousel by index
 */
function onGoTo(idx: number) {
  selectedIdx.value = clampImageIdx(idx)
}

/**
 * Toggles whether to zoom in on the selected image or not.
 */
function onToggleZoom() {
  isZoomedIn.value = !isZoomedIn.value
}

/**
 * On android back button press, close zoom if open, otherwise close the dialogue itself.
 */
function onAndroidBack() {
  if (isZoomedIn.value) {
    onToggleZoom()
  } else {
    onDialogCancel()
  }
}

//endregion

//region Setup Code

// Enable pinch zooming when we are zoomed in
watch(
  isZoomedIn,
  (newIsZoomedIn) => {
    if (newIsZoomedIn) {
      pinchZoom = new PinchZoom(pinchZoomTarget.value?.$el)
      pinchZoom.enable()
    } else {
      pinchZoom?.destroy()
    }
  },
  { flush: "post" }
)
//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_carousel_slide = _resolveComponent("q-carousel-slide")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_carousel_control = _resolveComponent("q-carousel-control")!
  const _component_q_carousel = _resolveComponent("q-carousel")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin column no-wrap" }, {
        default: _withCtx(() => [
          _createVNode(SfxTopNavigation, {
            title: _unref(t)('imageViewOverlay.title'),
            "navigation-action": _unref(NavigationAction).CLOSE,
            onClose: _unref(onDialogCancel),
            class: "shadow",
            "hide-context-menu": "",
            "hide-separator": "",
            "hide-notifications": "",
            "quick-action": isZoomedIn.value ? _unref(ContextAction).IMAGE_ZOOM_OUT : _unref(ContextAction).IMAGE_ZOOM_IN,
            onContextSelect: _cache[0] || (_cache[0] = ($event: any) => (onToggleZoom()))
          }, {
            subtitle: _withCtx(() => [
              _createTextVNode(_toDisplayString(selectedImage.value.attachmentData.name), 1)
            ]),
            _: 1
          }, 8, ["title", "navigation-action", "onClose", "quick-action"]),
          (isZoomedIn.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(_component_q_carousel_slide, {
                  name: selectedImage.value.attachmentData.name,
                  class: "image-overlay--slide-fullscreen full-height full-width"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(QImg), {
                      ref_key: "pinchZoomTarget",
                      ref: pinchZoomTarget,
                      src: selectedImage.value.src,
                      fit: "contain",
                      class: "fit"
                    }, null, 8, ["src"])
                  ]),
                  _: 1
                }, 8, ["name"])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(_component_q_carousel, {
                  ref: "carousel",
                  modelValue: selectedIdx.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedIdx).value = $event)),
                  class: "transparent full-width",
                  swipeable: "",
                  infinite: "",
                  animated: "",
                  "transition-prev": "slide-right",
                  "transition-next": "slide-left"
                }, {
                  control: _withCtx(() => [
                    _createVNode(_component_q_carousel_control, {
                      position: "left",
                      class: "column justify-center"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(SfxBtnOutline, {
                          class: "bg-white",
                          icon: _unref(Icons).CHEVRON_LEFT,
                          onClick: _cache[1] || (_cache[1] = ($event: any) => ((_ctx.$refs.carousel! as any).previous()))
                        }, null, 8, ["icon"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_q_carousel_control, {
                      position: "right",
                      class: "column justify-center"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(SfxBtnOutline, {
                          class: "bg-white",
                          icon: _unref(Icons).CHEVRON_RIGHT,
                          onClick: _cache[2] || (_cache[2] = ($event: any) => ((_ctx.$refs.carousel! as any).next()))
                        }, null, 8, ["icon"])
                      ]),
                      _: 1
                    })
                  ]),
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.images, (image, index) => {
                      return (_openBlock(), _createBlock(_component_q_carousel_slide, {
                        key: index,
                        name: index
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(QImg), {
                            src: image.src,
                            fit: "contain",
                            class: "fit",
                            ratio: "1"
                          }, {
                            error: _withCtx(() => [
                              _createElementVNode("div", _hoisted_3, [
                                _createVNode(_component_q_icon, {
                                  name: _unref(Icons).IMAGE_ERROR,
                                  class: "text-disabled",
                                  size: "48px"
                                }, null, 8, ["name"]),
                                _createElementVNode("i", _hoisted_4, _toDisplayString(_ctx.$t("sfxImage.no_image")), 1)
                              ])
                            ]),
                            _: 2
                          }, 1032, ["src"])
                        ]),
                        _: 2
                      }, 1032, ["name"]))
                    }), 128))
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(date).formatDate(selectedImage.value.attachmentData.created_dt, _unref(DATE_TIME_FULL_MASK))), 1),
                _createVNode(_component_q_list, {
                  ref: "thumbnails",
                  class: "row no-wrap scroll image-gallery q-pb-md q-pl-lg q-pr-lg",
                  style: {"max-width":"100%"}
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.images, (image, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "image-gallery-item",
                        key: index
                      }, [
                        _createVNode(_unref(QImg), {
                          src: image.src,
                          height: "72px",
                          width: "72px",
                          class: _normalizeClass(["image-gallery-item--image image-overview-thumbnail", { 'image-gallery-item--image-selected': index === selectedIdx.value }]),
                          onClick: ($event: any) => (onGoTo(index))
                        }, {
                          error: _withCtx(() => [
                            _createElementVNode("div", _hoisted_6, [
                              _createVNode(_component_q_icon, {
                                name: _unref(Icons).IMAGE_ERROR,
                                class: "text-disabled",
                                size: "25px"
                              }, null, 8, ["name"]),
                              _createElementVNode("i", _hoisted_7, _toDisplayString(_ctx.$t("sfxImage.no_image_small")), 1)
                            ])
                          ]),
                          _: 2
                        }, 1032, ["src", "class", "onClick"])
                      ]))
                    }), 128))
                  ]),
                  _: 1
                }, 512)
              ]))
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})